<script setup lang="ts">
import { onMounted, watch } from 'vue';
import { IonApp, IonRouterOutlet } from '@ionic/vue';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar'

import { useCookieConsent } from '@/composables/useCookieConsent';
import { usePushNotifications } from '@/composables/usePushNotifications';
import { restoreAndApplyConsent } from '@/helper/cookie-consent/application';
import { useAuthStore } from '@/stores/auth';
import { useStoriesStore } from '@/stores/stories';

const authStore = useAuthStore();
const storiesStore = useStoriesStore();
const { initializePushNotifications, isPlatform } = usePushNotifications();
const { getCookieConsentInstance, exposeCookieConsentToWindow } =
  useCookieConsent();

const setLoggedInClass = () => {
  if (authStore.user === null) {
    document.documentElement.classList.remove('logged-in');
    return;
  }
  document.documentElement.classList.add('logged-in');
};

const setupCookieConsent = () => {
  exposeCookieConsentToWindow();
  const $cc = getCookieConsentInstance();

  /**
   * Workaround: Apple restricts cookie usage but the consent lib uses them.
   * We store settings in localStorage and restore on load.
   */
  const shouldHideModal = isPlatform('ios');
  restoreAndApplyConsent($cc, shouldHideModal);
};

const setStatusBarStyle = async () => {
  // Style.Dark forces white icons/text
  await StatusBar.setStyle({ style: Style.Dark });
};

onMounted(() => {
  if (Capacitor.isNativePlatform()) {
    setStatusBarStyle();
  }
  setupCookieConsent();
  setLoggedInClass();
  initializePushNotifications();
  storiesStore.fetchStories();
});

watch(
  () => authStore.user,
  () => setLoggedInClass(),
);
</script>

<template>
  <ion-app>
    <ion-router-outlet />
  </ion-app>
</template>

<style lang="less">
@font-face {
  font-display: swap;
  font-family: Gotham Rounded;
  font-style: normal;
  font-weight: 600;
  src: url('./assets/fonts/Gotham/GothamRnd-Bold.woff2') format('woff2');
}

@font-face {
  font-display: swap;
  font-family: Gotham Rounded;
  font-style: normal;
  font-weight: 500;
  src: url('./assets/fonts/Gotham/GothamRnd-Medium.woff2') format('woff2');
}

@font-face {
  font-display: swap;
  font-family: Gotham Rounded;
  font-style: normal;
  font-weight: 400;
  src: url('./assets/fonts/Gotham/GothamRnd-Book.woff2') format('woff2');
}

@font-face {
  font-display: swap;
  font-family: Gotham Rounded;
  font-style: normal;
  font-weight: 350;
  src: url('./assets/fonts/Gotham/GothamRnd-Light.woff2') format('woff2');
}

@font-face {
  font-display: swap;
  font-family: Gotham Rounded;
  font-style: normal;
  font-weight: 300;
  src: url('./assets/fonts/Gotham/GothamRnd-Light.woff2') format('woff2');
}

* {
  font-family: 'Gotham Rounded', Arial, 'Helvetica Neue', Helvetica, sans-serif;
}
.font--light {
  font-weight: 300;
}

a[target='_blank'],
a.icon-external-link {
  position: relative;
  color: inherit;

  &::after {
    content: '';
    display: inline-block;
    width: 18px;
    height: 18px;
    background-image: url('~@/assets/img/icons/external-link.svg');
    background-repeat: no-repeat;
    background-size: contain;
    margin-left: 6px;
    position: relative;
    top: 4px;
  }
}
</style>
